'use client';

import React, { Component } from 'react';
import Link from 'next/link';
import { withAppRouter } from '@/legacy/withAppRouter';

import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import {
	CHECKOUT_STEP_AUTH,
	CHECKOUT_STEP_SELECT_PAYMENT_DETAILS,
	CHECKOUT_STEP_SHIPPING_ADDRESS,
} from '@/constants/checkout';
import {
	ERR_5500,
	ERROR_PAYMENT_CARD_NOT_PROCESSED,
	ERROR_PAYMENT_METHOD_NOT_LOADED, 
} from '@/constants/errors';
import {
	TX_BILLING_ADDRESS,
	TX_CART_SUMMARY_TITLE,
	TX_CHECKOUT_BILLING_ADDRESS_SAME,
	TX_CHECKOUT_PLACE_ORDER,
	TX_CHECKOUT_PLACING_ORDER,
	TX_CONTACT_US,
} from '@/constants/strings';
import { 
	URL_CONTACT_US,
	URL_SUCCESS,
} from '@/constants/urls';

import { loadSquareCardPayments } from '@/lib/square-card-payment';

import { Order } from '@/models/orders';
import { SquareCCPaymentMethod } from '@/models/payments';

import {
	isLoggedIn,
} from '@/utils/auth';
import { 
	checkoutGetIsStoreCreditApplied,
	checkoutGetSelectedPaymentMethod,
	checkoutGetSelectedShippingMethods,
	getCartGrandTotal, 
	getCheckoutStepData, 
} from '@/utils/checkout';
import { getCurrencyCode } from '@/utils/currency';
import { isFormValid } from '@/utils/form-validation';
import { formatServerError } from '@/utils/formatting';
import { getStoreLanguage } from '@/utils/language';
import { getUrlParams } from '@/utils/request';

import CheckoutTotals from '@/components/Cart/totals/CheckoutTotals';
import Checkbox from '@/components/Input/Checkbox';
import LoadingIcon from '@/components/Icons/LoadingIcon';
import CheckoutStepPaymentDetailsBillingAddressInput from '@/components/Checkout/blocks/steps/payment-details/CheckoutStepPaymentDetailsBillingAddressInput';

// import stepStyles from '../_styles/CheckoutStepComponent.module.scss';
// import styles from './_styles/SquareCCPaymentInput.module.scss';

import '@/components/Settings/_styles/_paymentmethods.scss';

import * as checkoutActionCreators from '@/actions/checkout';
let allActionCreators = Object.assign({}, checkoutActionCreators);

export class SquareCCPaymentInput extends Component {

	constructor(props) {
		super(props);

		this.state = {

			inputSameBilling: true,
			inputBillingAddress: {},

			errorBillingAddress: '',

			submitReqError: null,
			submitReqPending: false,

			saveSignal: 0,
			validationSignal: 0,

			squareLoading: true,
		};

		this.paymentMethod = checkoutGetSelectedPaymentMethod(this.props.checkout.stepData);
		this.shippingMethods = checkoutGetSelectedShippingMethods(this.props.checkout.stepData);
		this.shippingAddress = this.getShippingAddress();

		this.square = null;
		this.squarePayments = null;
		this.squareCard = null;

		this.checkoutColorRef = React.createRef();
		this.checkoutAccentRef = React.createRef();
		this.squareInputRef = React.createRef();

		this.checkNav = this.checkNav.bind(this);
		this.initCardPayments = this.initCardPayments.bind(this);
	}

	componentDidMount() {
		window.addEventListener('beforeunload', this.checkNav);
		loadSquareCardPayments(this.initCardPayments);
	}

	componentWillUnmount() {
		document.body.style.overflow = 'visible';
		window.removeEventListener('beforeunload', this.checkNav);
		if(this.squareInterval) {
			clearInterval(this.squareInterval);
		}
		if(this.squareCard) {
			this.squareCard.destroy();
		}
	}

	checkNav(evt) {
		if(this.state.submitReqPending) {
			evt.preventDefault();
		}
		return '';
	}

	loadingStart(cb) {
		document.body.style.overflow = 'hidden';
		this.setState({
			submitReqError: null,
			submitReqPending: true,
		}, () => {
			if(cb) { cb(); }
		});
	}

	loadingEnd(err, cb) {
		document.body.style.overflow = 'visible';
		this.setState({
			submitReqError: err || null,
			submitReqPending: false,
		}, () => {
			if(cb) { cb(); }
		});
	}

	// Leaves this.square null, which renders the "payment method not loaded"
	// error instead of the card-field spinner.
	initCardPaymentsFailed(err) {
		if(err) { console.error(err); }
		this.square = null;
		this.setState({ squareLoading: false });
	}

	async initCardPayments() {

		if(!window.Square || !this.props.paymentMethod) {
			this.initCardPaymentsFailed();
			return null;
		}

		try {
			this.square = window.Square;

			const squarePaymentModel = new SquareCCPaymentMethod();

			// payments() throws on unsupported browsers and bad app/location IDs
			this.squarePayments = this.square.payments(squarePaymentModel.applicationId, squarePaymentModel.locationId);
			await this.squarePayments.setLocale(this.getLanguage());

			const squareStyle = this.getSquareStyleObj();
			this.squareCard = await this.squarePayments.card({
				style: squareStyle,
			});
		} catch(err) {
			this.initCardPaymentsFailed(err);
			return null;
		}

		this.squareInterval = setInterval(async () => {
			if(this.squareInputRef && this.squareInputRef.current) {
				this.setState({
					squareLoading: false, 
				}, async () => {
					await this.squareCard.attach('#squareCardContainer')
						.catch((err) => {
						// Do nothing; error occurs if Stipe form already attached to page
						// Just don't need the error in the console
						});
					clearInterval(this.squareInterval);
				});
			}
		}, 200);
	}

	getLanguage() {
		const { i18n } = this.props;
		return getStoreLanguage(i18n);
	}

	getSquareStyleObj() {
		
		const inputContainer = {
			borderRadius: '0',
		};
		const inputContainerFocus = {};
		const messageIcon = {};

		if(this.checkoutColorRef && this.checkoutColorRef.current) {
			const foundColor = getComputedStyle(this.checkoutColorRef.current).color;
			if(foundColor) {
				inputContainer['borderColor'] = foundColor;
			}
		}

		if(this.checkoutAccentRef && this.checkoutAccentRef.current) {
			const foundAccent = getComputedStyle(this.checkoutAccentRef.current).color;
			if(foundAccent) {
				inputContainerFocus['borderColor'] = foundAccent;
				messageIcon['color'] = foundAccent;
			}
		}

		return {
			'.input-container': inputContainer,
			'.input-container.is-focus': inputContainerFocus,
			'.message-icon': messageIcon,
		};
	}

	toggleSameAsShipping() {
		this.setState({ inputSameBilling: !this.state.inputSameBilling });
	}

	validateAll() {
		// Nothing to validate in CC form- Square does it
		const errorObj = {};
		this.setState(errorObj);
		return isFormValid(errorObj);
	}

	handleSubmit(evt) {
		if(evt) { evt.preventDefault(); }

		if(this.validateAll()) {
			if(this.state.inputSameBilling) {
				this.completeSave();
			} else {
				this.setState({ saveSignal: Date.now() });
			}
		} else if(this.state.inputSameBilling === false) {
			this.setState({ validationSignal: Date.now() });
		}
	}

	getShippingAddress() {
		const stepData = getCheckoutStepData(CHECKOUT_STEP_SHIPPING_ADDRESS, this.props.checkout.stepData);
		if(stepData && stepData.data && stepData.data.shippingAddress) {
			return stepData.data.shippingAddress;
		}
		return null;
	}

	getBillingAddress(savedBillingAddress = null) {
		if(this.state.inputSameBilling === true) {
			const stepData = getCheckoutStepData(CHECKOUT_STEP_SHIPPING_ADDRESS, this.props.checkout.stepData);
			if(stepData && stepData.data && stepData.data.shippingAddress) {
				return stepData.data.shippingAddress;
			}
		} else {
			// The billing form hands over the address it just saved. It has also
			// written it to step data, but this.props may not show it yet; reading
			// only from props found nothing, so the first Place Order hung forever.
			if(savedBillingAddress) {
				return savedBillingAddress;
			}
			const stepData = getCheckoutStepData(CHECKOUT_STEP_SELECT_PAYMENT_DETAILS, this.props.checkout.stepData);
			if(stepData && stepData.data && stepData.data.billingAddress) {
				return stepData.data.billingAddress;
			}
		}
		return null;
	}

	getNoteString() {
		const emailAddress = this.getOrderEmail();
		// Intentionally not translated; should be in the store language
		// TODO: translate (TX_ORDER_ONLINE_ORDER) based on store language, not user language
		return emailAddress ? `Online Order - ${emailAddress}` : `Online Order`;
	}

	getOrderEmail() {
		if(isLoggedIn()) {
			return this.props.user.user.email;
		} else {
			const authStepData = getCheckoutStepData(CHECKOUT_STEP_AUTH, this.props.checkout.stepData);
			if(authStepData && authStepData.data && authStepData.data.isGuest) {
				return authStepData.data.guestUser.email;
			}
		}
		return '';
	}

	// savedBillingAddress: passed by the billing form when the customer uses a
	// separate billing address (its saveComplete callback).
	async completeSave(savedBillingAddress = null) {

		// Every exit from this callback must end loading: the "Placing order"
		// screen is a full-page overlay, so a path that returns without
		// loadingEnd() leaves the customer on an endless spinner.
		this.loadingStart(async () => {
			try {
				await this.submitOrder(savedBillingAddress);
			} catch(err) {
				console.error(err);
				this.loadingEnd(ERR_5500);
			}
		});
	}

	async submitOrder(savedBillingAddress) {

		const billingAddress = this.getBillingAddress(savedBillingAddress);

		// fetchToken / fetchVerification end loading themselves on failure
		const tokenResp = await this.fetchToken();
		if(!tokenResp) { return null; }

		const verificationResp = await this.fetchVerification(tokenResp.token, billingAddress);
		if(!verificationResp) { return null; }

		const paymentData = {
			source_id: tokenResp.token,
			verification_token: verificationResp.token,
			note: this.getNoteString(),
		}

		// Read the shipping address from step data at submit time rather
		// than the snapshot taken in the constructor — covers the case
		// where the user enters the address after this component mounts.
		const submitShippingAddress = this.getShippingAddress();

		const shippingMethodUuids = this.shippingMethods
			.filter((locationMethod) => {
				return locationMethod.shippingMethod.publicUuid && locationMethod.location.publicUuid;
			})
			.map((locationMethod) => {
				return {
					shipping_method_uuid: locationMethod.shippingMethod.publicUuid,
					location_uuid: locationMethod.location.publicUuid,
				};
			});

		const orderData = {
			shipping_address_uuid: submitShippingAddress?.publicUuid ?? null,
			shipping_method_uuids: shippingMethodUuids,
			billing_address_uuid: billingAddress.publicUuid,
			payment_method_uuid: this.paymentMethod.publicUuid,
			cart_uuid: this.props.cart.currentCart.publicUuid,
			payment_record_uuid: null,
			status: this.props.cart.currentCart.hasPreorderItems() ? Order.preorderStatus : Order.initialStatus,
			use_store_credit: checkoutGetIsStoreCreditApplied(this.props.checkout.stepData),
		};

		const combinedData = Object.assign({}, orderData, paymentData);

		let orderReqFailed = false;
		const orderResp = await this.props.checkoutPlaceOrder(combinedData)
			.catch((errResp) => {
				console.error(errResp);
				orderReqFailed = true;
				this.loadingEnd(formatServerError(errResp));
			});

		if(orderReqFailed) { return null; }
		if(!orderResp || !orderResp.public_uuid) {
			this.loadingEnd(ERR_5500);
			return null;
		}

		this.props.checkoutClearStepData();

		const uuidParams = {
			order: orderResp.public_uuid,
		}
		this.props.router.push(`${URL_SUCCESS}${getUrlParams(uuidParams)}`);
	}

	async fetchToken() {
		try {
			const tokenResult = await this.squareCard.tokenize();
			if(tokenResult.status === 'OK') {
				return tokenResult;
			}
			// Square also marks bad fields inside the card element, but on a
			// phone that can be scrolled out of view — always say something.
			this.loadingEnd(ERROR_PAYMENT_CARD_NOT_PROCESSED);
			return null;
		} catch(err) {
			console.error(err);
			this.loadingEnd(ERROR_PAYMENT_CARD_NOT_PROCESSED);
			return null;
		}
	}

	async fetchVerification(token, billingAddress) {
		try {

			if(!billingAddress) {
				this.loadingEnd(ERR_5500);
				return null;
			}

			const verificationDetails = {
				amount: (getCartGrandTotal(this.props.cart.currentCart, this.props.checkout.stepData)).toString(),
				billingContact: {
					addressLines: billingAddress.streetArray,
					familyName: billingAddress.lastName,
					givenName: billingAddress.firstName,
					email: this.getOrderEmail(),
					country: billingAddress.country.key,
					phone: billingAddress.phone,
					region: billingAddress.region,
					city: billingAddress.city,
				},
				currencyCode: getCurrencyCode(),
				intent: 'CHARGE',
			};

			const verificationResults = await this.squarePayments.verifyBuyer(
				token,
				verificationDetails
			);
			if(!verificationResults) {
				this.loadingEnd(ERROR_PAYMENT_CARD_NOT_PROCESSED);
				return null;
			}
			return verificationResults;

		} catch(err) {
			console.error(err);
			this.loadingEnd(ERROR_PAYMENT_CARD_NOT_PROCESSED);
			return null;
		}
	}

	render() {

		const {t} = this.props;

		return <div className={'SquareCCPaymentInput CheckoutStepComponent'}>
			<div className='checkoutColor' ref={this.checkoutColorRef} />
			<div className='checkoutAccent' ref={this.checkoutAccentRef} />
			<div className='pmiLiner'>
				{this.state.squareLoading === true ?
					<div className='squareLoading'>
						<LoadingIcon iconClass='iconElement' />
					</div> :
					<>
						{this.square === null && (
							<div className='squareLoadError'>
								<div className='squareLoadErrorTitle'>{t(ERROR_PAYMENT_METHOD_NOT_LOADED)}</div>
								<div className='squareLoadErrorLinkWrapper'>
									<Link href={URL_CONTACT_US}>{t(TX_CONTACT_US)}</Link>
								</div>
							</div>
						)}
					</>
				}
				<div className={this.state.submitReqError ? 'squareServerError present' : 'squareServerError'}>
					{this.state.submitReqError ? t(this.state.submitReqError) : ''}
				</div>
				<form 
					className={'checkoutStepInputForm'} 
					onSubmit={this.handleSubmit.bind(this)}
					style={{
						display: this.state.squareLoading === true || this.square === null ? 'none' : 'block',
					}}>
					<div className={'paymentSection'}>
						<div id='squareCardContainer' ref={this.squareInputRef}></div>
						<div className={'checkoutFormInput sameAsShippingWrapper'}>
							<div className='sameAsShippingInputWrapper'>
								<Checkbox 
									name={t(TX_BILLING_ADDRESS)}
									id={'sameAsShippingInput'}
									value={'same-as-shipping'}
									checked={this.state.inputSameBilling}
									onChange={this.toggleSameAsShipping.bind(this)} />
							</div>
							<label 
								htmlFor={'sameAsShippingInput'}
								className='sameAsShippingValueWrapper'>
								{t(TX_CHECKOUT_BILLING_ADDRESS_SAME)}
							</label>
						</div>
					</div>
					{this.state.inputSameBilling === false && (
						<div className={'billingSection'}>
							<CheckoutStepPaymentDetailsBillingAddressInput
								config={this.props.config}
								setStepData={this.props.setStepData}
								saveSignal={this.state.saveSignal}
								saveComplete={this.completeSave.bind(this)}
								validationSignal={this.state.validationSignal} />
						</div>
					)}
					<div className='checkoutSummaryWrapper'>
						<div className='summaryTitle'>{t(TX_CART_SUMMARY_TITLE)}</div>
						<CheckoutTotals
							cartLoading={this.state.cartLoading} />
					</div>
					<div className={'checkoutFormInput checkoutFormAction placeOrderAction'}>
						<button 
							type='submit'
							className='checkoutFormButton'
							disabled={this.state.submitReqPending}>
							{t(TX_CHECKOUT_PLACE_ORDER)}
						</button>
					</div>
				</form>
			</div>
			<div className={`checkoutSubmitScreen ${this.state.submitReqPending ? 'active' : ''}`}>
				<div className='submitScreenLiner'>
					<div className='submitIconWrapper'>
						<LoadingIcon />
					</div>
					<div className='submitIconLabel'>{t(TX_CHECKOUT_PLACING_ORDER)}</div>
				</div>
			</div>
		</div>;
	}
}

function mapStateToProps(state) {
	return {
		cart: state.cart, 
		checkout: state.checkout,
		user: state.user,
	};
}

export default connect(mapStateToProps, allActionCreators)(withTranslation()(withAppRouter(SquareCCPaymentInput)));